import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelectedCompany } from './SelectedCompanyContext';
import { listenReservationsForWeek, createReservation, deleteOwnReservationForHour } from '../Api/ReservationsApi';
import { startOfWeek, endOfWeek, addWeeks, format } from 'date-fns';
import { fi } from 'date-fns/locale';

function dayKey(date) {
  return format(date, 'eee dd.MM.yyyy', { locale: fi });
}

const ReservationsContext = createContext(undefined);

export function ReservationsProvider({ children }) {
  const { selectedCompanyId } = useSelectedCompany();
  const [activeCalendarId, setActiveCalendarId] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [weekEnd, setWeekEnd] = useState(endOfWeek(new Date(), { weekStartsOn: 1 }));
  const [reservationsByDay, setReservationsByDay] = useState({});
  const [loading, setLoading] = useState(false);

  function nextWeek() {
    const ns = addWeeks(weekStart, 1);
    setWeekStart(ns);
    setWeekEnd(endOfWeek(ns, { weekStartsOn: 1 }));
  }
  function prevWeek() {
    const ps = addWeeks(weekStart, -1);
    setWeekStart(ps);
    setWeekEnd(endOfWeek(ps, { weekStartsOn: 1 }));
  }

  useEffect(() => {
    setReservationsByDay({});
  }, [activeCalendarId, selectedCompanyId, weekStart, weekEnd]);

  useEffect(() => {
    if (!activeCalendarId) return;
    setLoading(true);
    let unsub = null;
    try {
      unsub = listenReservationsForWeek(
        activeCalendarId,
        selectedCompanyId, // ignored in API, ok to pass
        weekStart,
        weekEnd,
        (items, err) => {
          if (err) {
            console.error('listenReservationsForWeek callback error:', err);
            setReservationsByDay({});
            setLoading(false);
            return;
          }
          const map = {};
          items.forEach(it => {
            const k = dayKey(it.päivämäärä.toDate ? it.päivämäärä.toDate() : it.päivämäärä);

            const rawHour = it.kellonaika;
            let hourNum = typeof rawHour === 'number' ? rawHour : undefined;
            if (hourNum === undefined) {
              const m = String(rawHour ?? '').match(/\b(\d{1,2})\b/);
              hourNum = m ? Number(m[1]) : NaN;
            }
            const idx = (Number.isFinite(hourNum) ? hourNum : 0) - 6; // 0..15

            if (!map[k]) map[k] = {};
            if (idx >= 0 && idx <= 15) {
              map[k][idx] = {
                id: it.käyttäjäid,
                talonyhtiöid: it.talonyhtiöid,
                sukunimi: it.sukunimi || '',
                hinta: Number(it.hinta || 0)
              };
            }
          });
          setReservationsByDay(map);
          setLoading(false);
        }
      );
    } catch (err) {
      console.error('ReservationsProvider effect error:', err);
      setLoading(false);
    }
    return () => { try { unsub && unsub(); } catch (_) {} };
  }, [activeCalendarId, selectedCompanyId, weekStart, weekEnd]);

  async function book({ date, hour, user, companyId, calendarId, price }) {
    try {
      await createReservation({
        date,
        hour,
        userId: user.uid,
        firstName: user.etunimi,
        lastName: user.sukunimi,
        companyId,
        calendarId,
        price
      });
    } catch (err) {
      console.error('createReservation error:', err);
      // ei näytetä käyttäjälle mitään
    }
  }

  async function cancel({ date, hour, userId }) {
    try {
      await deleteOwnReservationForHour(userId, date, hour);
    } catch (err) {
      console.error('deleteOwnReservationForHour error:', err);
      // ei näytetä käyttäjälle mitään
    }
  }

  const value = useMemo(() => ({
    activeCalendarId,
    setActiveCalendarId,
    weekStart,
    weekEnd,
    nextWeek,
    prevWeek,
    reservationsByDay,
    book,
    cancel,
    loading
  }), [activeCalendarId, weekStart, weekEnd, reservationsByDay, loading]);

  return (
    <ReservationsContext.Provider value={value}>
      {children}
    </ReservationsContext.Provider>
  );
}

export function useReservations() {
  const ctx = useContext(ReservationsContext);
  if (!ctx) throw new Error('useReservations must be used inside ReservationsProvider');
  return ctx;
}
